/**
 * Parcours évaluateurs d'une campagne — portail admin (plan du 2026-08-26).
 *
 * Deuxième descente possible depuis une campagne, à côté des parcours candidats
 * (`CampagneDetailAdmin`) : les évaluateurs et le compte de leurs notations. Le
 * détail d'un évaluateur, avec ses notations, reste `/notations/:peid`.
 *
 * Page dédiée plutôt qu'onglet du composant partagé `@repo/campagne` : le
 * portail client n'a rien à voir ici, et une URL propre garantit que ces
 * données ne sont lues que si on les demande.
 */
import {
  Breadcrumb,
  DataTable,
  PageContainer,
  PageHeader,
  useFiltresColonnesUrl,
} from "@repo/ui";
import { usePaginatedQuery, useQuery } from "convex/react";
import { useMemo } from "react";
import { useParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { arianeOrganisation } from "../../lib/ariane";
import { LoadMore } from "../../components/LoadMore";
import {
  colonnesEvaluateurs,
  FACETTES_EVALUATEURS,
} from "./colonnesEvaluateurs";

/** Références stables : recréées à chaque rendu, elles feraient boucler les mémos. */
const CHAMPS_FILTRES = {
  acteur: { colonne: "acteur", multi: true },
  statut: { colonne: "statut", multi: true },
} as const;
const BARRE_FILTRES = { facettes: FACETTES_EVALUATEURS };

/** Voir `CampagnesOrganisation` : le tri client porte sur ce qui est chargé. */
const TAILLE_PREMIERE_PAGE = 50;

export function EvaluateursCampagne() {
  const { id } = useParams<{ id: string }>();
  const campagneId = id as Id<"campagnes">;

  const [filtres, setFiltres] = useFiltresColonnesUrl(CHAMPS_FILTRES);

  const { results, status, loadMore } = usePaginatedQuery(
    api.superAdmin.listerParcoursEvaluateursSuperAdmin,
    { filtreCampagneId: campagneId },
    { initialNumItems: TAILLE_PREMIERE_PAGE },
  );

  const campagne = useQuery(api.campagnes.obtenirCampagneSuperAdmin, {
    campagneId,
  });

  const colonnes = useMemo(() => colonnesEvaluateurs(), []);

  return (
    <PageContainer>
      <Breadcrumb
        items={[
          ...arianeOrganisation(campagne?.organisationId, campagne?.organisationNom, "campagnes"),
          { label: campagne?.nom ?? "Campagne", href: `/campagnes/${campagneId}` },
          { label: "Évaluateurs" },
        ]}
      />

      <PageHeader
        titre="Évaluateurs"
        sousTitre={
          status === "LoadingFirstPage"
            ? "Chargement…"
            : `${results.length} évaluateur${results.length > 1 ? "s" : ""}`
        }
      />

      <DataTable
        colonnes={colonnes}
        donnees={status === "LoadingFirstPage" ? undefined : results}
        cleColonnes="evaluateurs-campagne"
        hrefLigne={(ligne) => `/notations/${ligne._id}`}
        barreFiltres={BARRE_FILTRES}
        filtres={filtres}
        onFiltresChange={setFiltres}
        vide={{
          titre: "Aucun évaluateur",
          description:
            "Cette campagne n'a pas encore de parcours évaluateur. Ils sont créés avec les notations.",
        }}
        videFiltre={{ titre: "Aucun évaluateur avec ces critères" }}
      />

      <LoadMore status={status} loadMore={loadMore} count={results.length} />
    </PageContainer>
  );
}
